import React, { useState } from "react";

import { InfoSmIcon as InfoSm, Typography } from "@packages/ds";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import Error from "Components/Error";
import { Button, ButtonWrapper, IconControl } from "ds/Button";
import * as Code from "ds/Code";
import Dialog from "ds/Dialog";
import Modal from "ds/Modal";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { goToEnvironment, capitalize } from "Libs/utils";
import { environmentLoadingSelector } from "Reducers/environment";
import {
  redeploy,
  clearRedeploy,
  redeployStateSelector
} from "Reducers/environment/actions/redeploy";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./redeploy.styles";

const Redeploy = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const [isRedirecting, setIsRedirecting] = useState(false);

  const { isLoading, error, data } = useAppSelector(redeployStateSelector);
  const isEnvironmentLoading = useSelectorWithUrlParams(
    environmentLoadingSelector,
    {
      organizationId,
      projectId,
      environmentId
    }
  );

  if (data && !isRedirecting) {
    setIsRedirecting(true);
    dispatch(clearRedeploy());
    goToEnvironment(navigate, organizationId, projectId, environmentId);
  }

  const commandName = intl.formatMessage({
    id: "CLI_command_name"
  });

  const command = intl.formatMessage(
    { id: "redeploy.command" },
    {
      commandName
    }
  );

  const onClose = (): void => {
    goToEnvironment(navigate, organizationId, projectId, environmentId);
  };

  return (
    <Modal
      isOpen={true}
      size="medium"
      onOpenChange={onClose}
      handleClose={onClose}
    >
      <Typography.Title tag="h3">
        {intl.formatMessage({ id: "redeploy.modal.title" })}
      </Typography.Title>
      <div>
        {error?.message && <Error>{error.message}</Error>}
        <Typography.BodyText>
          <FormattedMessage id="redeploy.modal.text" />
        </Typography.BodyText>
        <S.NoteText weight="semiBold">
          <FormattedMessage id="redeploy.modal.note" />
        </S.NoteText>

        <S.DialogWrapper>
          <Typography.BodyText weight="semiBold">
            {intl.formatMessage({
              id: "redeploy.cli",
              defaultMessage: "CLI"
            })}
          </Typography.BodyText>
          <Dialog
            align="start"
            analyticId="redeploy.modal.dialog"
            title={intl.formatMessage({ id: "learnmore" })}
            description={intl.formatMessage({ id: "redeploy.modal.dialog" })}
            link={
              <Button
                analyticId="help_text"
                href={intl.formatMessage({
                  id: "links.documentation.help.CLI"
                })}
                variant="link"
              >
                {intl.formatMessage({
                  id: "help_text",
                  defaultMessage: "Help"
                })}
              </Button>
            }
            trigger={
              <IconControl>
                <InfoSm />
              </IconControl>
            }
          />
        </S.DialogWrapper>

        <Code.Block isCopyable singleLine id={command}>
          {command}
        </Code.Block>

        {isEnvironmentLoading || isLoading ? (
          <S.LoadingSuccess />
        ) : (
          <ButtonWrapper spacing="modal" justifyContent="end">
            <Button
              analyticId="cancel"
              id="environment-redeploy-cancel-btn"
              variant="secondary"
              onClick={onClose}
            >
              {capitalize(intl.formatMessage({ id: "cancel" }))}
            </Button>
            <Button
              analyticId="redeploy.button"
              id="environment-redeploy-save-btn"
              onClick={() => {
                dispatch(
                  redeploy({ organizationId, projectId, environmentId })
                );
              }}
            >
              {capitalize(intl.formatMessage({ id: "redeploy.button" }))}
            </Button>
          </ButtonWrapper>
        )}
      </div>
    </Modal>
  );
};

export default Redeploy;
